import { WorkflowSessionId } from '@customTypes/cloudmix/workflow'
import { MixerInputState } from '@customTypes/cloudmix/sessions/pads'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { applyPatches, enableES5 } from 'immer'
import { RootState } from '@lib/store'
import {
  COMPONENT_TYPES,
  isDestinationComponentType,
} from '@customTypes/cloudmix/sessions/components/base'

enableES5()

type ByWorkflowSessionId<T> = {
  byWorkflowSessionId: Record<WorkflowSessionId, T>
}

type InputStates = {
  byId: Record<string, MixerInputState>
  allIds: string[]
}

type Components = {
  byId: Record<string, any>
  allIds: string[]
}

type Inputs = {
  byId: Record<string, any>
  allIds: string[]
  byComponentId: Record<string, string[]>
}

type Links = {
  byId: Record<string, any>
  allIds: string[]
  bySource: Record<string, string>
  byTarget: Record<string, string>
}

type WorkflowSessionStates = {
  components: ByWorkflowSessionId<Components>
  inputs: ByWorkflowSessionId<Inputs>
  links: ByWorkflowSessionId<Links>
  inputState: ByWorkflowSessionId<InputStates>
}

export const initialState: WorkflowSessionStates = {
  components: {
    byWorkflowSessionId: {},
  },
  inputs: {
    byWorkflowSessionId: {},
  },
  links: {
    byWorkflowSessionId: {},
  },
  inputState: {
    byWorkflowSessionId: {},
  },
}

const workflowSessionState = createSlice({
  name: 'workflowSessionState',
  initialState,
  reducers: {
    updateFullState: (state, action) => {
      const { workflowSessionId, components, links, inputs, inputState } =
        action.payload

      state.components.byWorkflowSessionId[workflowSessionId] = components
      state.inputs.byWorkflowSessionId[workflowSessionId] = inputs
      state.links.byWorkflowSessionId[workflowSessionId] = links
      state.inputState.byWorkflowSessionId[workflowSessionId] = inputState
    },
    patchState: (state, action) => {
      const { workflowSessionId, patches } = action.payload

      try {
        applyPatches(
          state,
          patches.map((patch) => ({
            ...patch,
            path: [
              patch.path[0],
              'byWorkflowSessionId',
              workflowSessionId,
              ...patch.path.slice(1),
            ],
          }))
        )
      } catch (e) {
        console.error(e)
      }
    },
  },
})
export const { updateFullState, patchState } = workflowSessionState.actions
export default workflowSessionState.reducer

const emptyObj = {}
const emptyArr = []

export const selectInputBySessionId = createSelector(
  [
    (state: RootState) => state.workflowSessionState.inputs.byWorkflowSessionId,
    (_: any, workflowSessionId: WorkflowSessionId) => workflowSessionId,
  ],
  (inputs, workflowSessionId) => inputs[workflowSessionId]?.byId || {}
)

export const selectInputById = createSelector(
  [
    selectInputBySessionId,
    (_: any, _2: WorkflowSessionId, inputId: string) => inputId,
  ],
  (inputs, inputId) => inputs[inputId]
)

export const selectInputsByComponentId = createSelector(
  [
    (state: RootState) => state.workflowSessionState.inputs.byWorkflowSessionId,
    (_: RootState, workflowSessionId: WorkflowSessionId) => workflowSessionId,
    (_: RootState, _2: WorkflowSessionId, componentId: string) => componentId,
  ],
  (inputsBySession, workflowSessionId, componentId) => {
    const sessionInputs = inputsBySession[workflowSessionId]
    if (!sessionInputs) return []
    return sessionInputs.byComponentId?.[componentId] || []
  }
)

export const selectComponentBySessionId = createSelector(
  [
    (state: RootState) =>
      state.workflowSessionState.components.byWorkflowSessionId,
    (_: any, workflowSessionId: WorkflowSessionId) => workflowSessionId,
  ],
  (components, workflowSessionId) => components[workflowSessionId]?.byId || {}
)

export const selectComponentById = createSelector(
  [
    selectComponentBySessionId,
    (_: any, _2: WorkflowSessionId, componentId: string) => componentId,
  ],
  (components, componentId) => components[componentId]
)

export const selectLinksBySessionId = createSelector(
  [
    (state: RootState) => state.workflowSessionState.links.byWorkflowSessionId,
    (_: RootState, workflowSessionId: WorkflowSessionId) => workflowSessionId,
  ],
  (linksBySession, workflowSessionId) =>
    linksBySession[workflowSessionId] || {
      byId: {},
      byTarget: {},
      bySource: {},
      allIds: [],
    }
)

export const selectLinkById = createSelector(
  [
    selectLinksBySessionId,
    (_: RootState, _2: WorkflowSessionId, linkId: string) => linkId,
  ],
  (links, linkId) => links.byId[linkId] || emptyObj
)

export const selectLinkByTarget = createSelector(
  [
    selectLinksBySessionId,
    (_: RootState, _2: WorkflowSessionId, targetKey: string) => targetKey,
  ],
  (links, targetKey) => {
    const linkId = links.byTarget[targetKey]
    return linkId ? links.byId[linkId] : emptyObj
  }
)

export const selectLinkBySource = createSelector(
  [
    selectLinksBySessionId,
    (_: RootState, _2: WorkflowSessionId, sourceKey: string) => sourceKey,
  ],
  (links, sourceKey) => {
    const linkId = links.bySource[sourceKey]
    return linkId ? links.byId[linkId] : emptyObj
  }
)

export const selectDestinationComponents = createSelector(
  [
    (state: RootState, workflowSessionId: WorkflowSessionId) =>
      state.workflowSessionState.components.byWorkflowSessionId[
        workflowSessionId
      ]?.byId || emptyObj,
  ],
  (componentsById) =>
    Object.values(componentsById).filter(
      (component) =>
        component?.type != null && isDestinationComponentType(component.type)
    ) ?? emptyArr
)
